import React, { useState, useEffect } from 'react';
import {
  Button, Table, Modal, Input, Space, Card,
  Drawer, Form, DatePicker, Select,
} from 'antd';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import CancelledLeaveTable from './CancelledLeave';
import { UserService } from '../../Services/Manage/UserService';
import useMessage from 'antd/es/message/useMessage';
import { ENDPOINT } from '../../Services/ENDPOINT';
import LeaveService from "../../Services/Manage/LeaveService";

const { TextArea } = Input;
const { RangePicker } = DatePicker;

const LeaveRequest = () => {
  const [leaveType, setLeaveType] = useState('active');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cancelReasonInput, setCancelReasonInput] = useState('');
  const [currentRecordKey, setCurrentRecordKey] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [totalLeaveDays, setTotalLeaveDays] = useState(0);
  const [allLeaveData, setAllLeaveData] = useState([]);
  const [activeData, setActiveData] = useState([]);
  const [filteredActiveData, setFilteredActiveData] = useState([]);
  const [cancelledData, setCancelledData] = useState([]);
  const [message, msgContext] = useMessage();
  const [managers, setManagers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [form] = Form.useForm();
  const [currentUserUid, setCurrentUserUid] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('');

  const minDate = dayjs();
  const maxDate = dayjs().add(60, 'day');

  const disabledDate = (current) =>
    current < minDate.startOf('day') || current > maxDate.endOf('day');

  const assignKeys = (data) =>
    data.map((item, index) => ({
      ...item,
      key: item.id?.toString() || item.key?.toString() || `key-${index}`,
    }));

  const fetchActiveLeave = async (uid) => {
    if (!uid) return;
    try {
      const res = await LeaveService.getActiveLeave(uid);
      if (res?.status && Array.isArray(res.data)) {
        const dataWithKeys = assignKeys(res.data);

        const sortedData = dataWithKeys.sort(
          (a, b) => new Date(b.added_on) - new Date(a.added_on)
        );

        setAllLeaveData(sortedData);

        const actLeaves = sortedData.filter(item => item.status?.toLowerCase() === 'active');
        const cancLeaves = sortedData.filter(item => item.status?.toLowerCase() !== 'active');

        setActiveData(actLeaves);
        setFilteredActiveData(actLeaves);
        setCancelledData(cancLeaves);
      }
    } catch (error) {
      console.error('Failed to fetch leave requests:', error);
      message.error('Failed to load leave requests.');
    }
  };


  const fetchManagers = async () => {
    try {
      const res = await UserService.getAllUsers();
      const filtered = res.data
        .filter(user =>
          user.role?.toLowerCase() === 'manager' &&
          user.status?.toLowerCase() === 'active'
        )
        .map(user => {
          const uniqueId = user.uid || user.user_id || user.email || null;
          return { ...user, uid: uniqueId };
        })
        .filter(user => user.uid !== null);
      setManagers(filtered);
    } catch (error) {
      console.error('Failed to fetch managers:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await axios.get(ENDPOINT.Department);
      if (res.data.status && Array.isArray(res.data.data)) {
        setDepartments(res.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const fetchLeaveTypes = async () => {
    try {
      const res = await LeaveService.getLeaveType();
      if (res?.status && Array.isArray(res.data)) {
        setLeaveTypes(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch leave types:', error);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const user = await UserService.getOmeUser();
        if (user?.uid) {
          setCurrentUserUid(user.uid);
          await fetchActiveLeave(user.uid);
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
      fetchManagers();
      fetchDepartments();
      fetchLeaveTypes();
    };
    init();
  }, []);

  useEffect(() => {
    setFilteredActiveData(activeData);
  }, [activeData]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
    if (value) {
      const filtered = activeData.filter((item) =>
        Object.values(item).some(
          (val) => typeof val === 'string' && val.toLowerCase().includes(value)
        )
      );
      setFilteredActiveData(filtered);
    } else {
      setFilteredActiveData(activeData);
    }
  };

  const openCancelModal = (recordKey) => {
    setCurrentRecordKey(recordKey);
    setCancelReasonInput('');
    setIsModalOpen(true);
  };

  const handleModalOk = async () => {
    const recordToCancel = activeData.find((item) => item.key === currentRecordKey);
    if (recordToCancel) {
      try {
        const cancelPayload = {
          status: 'cancelled',
          comment: cancelReasonInput,
          updated_on: dayjs().format(),
        };

        const res = await LeaveService.cancelLeave(recordToCancel.id, cancelPayload);

        if (res.status) {
          message.success('Leave request cancelled successfully.');
          await fetchActiveLeave(currentUserUid);
        } else {
          message.error(res?.message || 'Failed to cancel leave.');
        }
      } catch (error) {
        console.error('Error cancelling leave:', error);
        message.error('Error cancelling leave.');
      }
    }
    setIsModalOpen(false);
  };

  const handleModalCancel = () => setIsModalOpen(false);
  const showDrawer = () => setDrawerVisible(true);
  const closeDrawer = () => {
    setDrawerVisible(false);
    setTotalLeaveDays(0);
    form.resetFields();
    setSelectedPaymentStatus('');
  };

  const onDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setTotalLeaveDays(dates[1].diff(dates[0], 'day') + 1);
    } else {
      setTotalLeaveDays(0);
    }
  };

  // handle leave type change
  const handleLeaveTypeChange = (value) => {
    const selectedLeave = leaveTypes.find(lt => lt.leave_type === value);
    if (selectedLeave) {
      setSelectedPaymentStatus(selectedLeave.payment_status || '');
    } else {
      setSelectedPaymentStatus('');
    }
  };

  const onFinish = async (values) => {
    try {
      const fromDateVal = values.dateRange[0].format('YYYY-MM-DD');
      const toDateVal = values.dateRange[1].format('YYYY-MM-DD');
      const user = await UserService.getOmeUser();
      const allUsers = await UserService.getAllUsers();

      const payload = {
        uid: user.uid,
        departmentid: allUsers.department_id,
        from_date: fromDateVal,
        to_date: toDateVal,
        note: values.reason || '',
        reporting_manager: values.manager,
        leave_type: values.leaveType,
        payment_status: selectedPaymentStatus,
        status: 'active',
        added_on: dayjs().format(),
      };

      const response = await LeaveService.createLeaveRequest(payload);

      if (response?.status && response?.data) {
        message.success(response.message || 'Leave request added successfully.');
        await fetchActiveLeave(user.uid);
      } else {
        message.error(response.message || 'Failed to add leave request.');
      }

      closeDrawer();
    } catch (error) {
      console.error('Error while adding leave request:', error);
      message.error('Error while adding leave request.');
    }
  };

  const activeColumns = [
    {
      title: 'S.No',
      key: 'serial',
      render: (_, __, index) => index + 1,
      align: 'center',
    },
    {
      title: 'Manager',
      dataIndex: 'reporting_manager',
      key: 'manager',
      align: 'center',
      render: (managerId) => {
        const manager = managers.find(m => m.uid === managerId);
        return manager ? manager.name : managerId;
      }
    },
    {
      title: 'Leave Type',
      dataIndex: 'leave_type',
      key: 'leaveType',
      align: 'center',
      render: (type) => type || '-',
    },
    // {
    //   title: 'Payment Status',
    //   dataIndex: 'payment_status',
    //   key: 'paymentStatus',
    //   align: 'center',
    //   render: (status) => status || '-',
    // },
    { title: 'From Date', dataIndex: 'from_date', key: 'fromDate', align: 'center' },
    { title: 'To Date', dataIndex: 'to_date', key: 'toDate', align: 'center' },
    {
      title: 'Raised On',
      dataIndex: 'added_on',
      key: 'raisedOn',
      align: 'center',
      render: (date) => dayjs(date).format('YYYY-MM-DD hh:mm A')
    },
    {
      title: 'Reason',
      dataIndex: 'note',
      key: 'reason',
      align: 'center',
      render: (text) => text || '-'
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      render: (_, record) => (
        <Space>
          <Button
            size="large"
            icon={<CloseOutlined />}
            style={{ border: '1px solid #ff4d4f', color: '#ff4d4f', padding: '8px 12px' }}
            onClick={() => openCancelModal(record.key)}
          >
            Cancel
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ minHeight: '100vh' }}>
      {msgContext}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 500 }}>Leave Request</h2>
          <Button type="primary" icon={<PlusOutlined />} size="large" onClick={showDrawer}>
            Create Leave Request
          </Button>
        </div>
        <Space style={{ marginBottom: 12 }}>
          <Button size="large" type={leaveType === 'active' ? 'primary' : 'default'} onClick={() => setLeaveType('active')}>Active Leave Request</Button>
          <Button size="large" type={leaveType === 'cancelled' ? 'primary' : 'default'} onClick={() => setLeaveType('cancelled')}>Other Leave Request</Button>
        </Space>
        {(leaveType === 'active' || leaveType === 'cancelled') && (
          <Card style={{ marginBottom: 16, padding: 12 }} bodyStyle={{ padding: 12 }}>
            <Input
              placeholder="Search"
              value={searchText}
              onChange={handleSearch}
              allowClear
              size="large"
              style={{ width: 350 }}
            />
          </Card>
        )}
        {leaveType === 'active' ? (
          <Table
            columns={activeColumns}
            dataSource={filteredActiveData}
            pagination={false}
            scroll={{ x: 'max-content' }}
            rowKey="key"
          />
        ) : (
          <CancelledLeaveTable
            data={cancelledData}
            searchText={searchText}
            managers={managers}
            departments={departments}
          />
        )}
      </Card>

      {/* Cancel Modal */}
      <Modal
        title="Write Reason for Cancellation"
        open={isModalOpen}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Submit"
        centered
      >
        <TextArea
          rows={4}
          value={cancelReasonInput}
          onChange={(e) => setCancelReasonInput(e.target.value)}
          placeholder="Write the reason for cancellation here..."
        />
      </Modal>

      {/* Drawer */}
      <Drawer
        title="Add New Leave Request"
        placement="right"
        onClose={closeDrawer}
        open={drawerVisible}
        width={360}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button onClick={closeDrawer} style={{ marginRight: 8 }}>Cancel</Button>
            <Button onClick={() => { form.submit(); }} type="primary">Submit</Button>
          </div>
        }
      >
        <Form layout="vertical" form={form} onFinish={onFinish}>
          <Form.Item
            name="dateRange"
            label="Date"
            rules={[{ required: true, message: 'Please select the date range' }]}
          >
            <RangePicker
              format="YYYY-MM-DD"
              style={{ width: '100%' }}
              disabledDate={disabledDate}
              onChange={onDateRangeChange}
            />
          </Form.Item>

          <Form.Item label="Total Leave Days">
            <Input value={totalLeaveDays} readOnly />
          </Form.Item>

          <Form.Item
            name="manager"
            label="Manager"
            rules={[{ required: true, message: 'Please select a manager' }]}
          >
            <Select placeholder="Select Manager" showSearch optionFilterProp="children">
              {managers.map((manager) => (
                <Select.Option key={manager.uid} value={manager.uid}>
                  {manager.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="leaveType"
            label="Leave Type"
            rules={[{ required: true, message: 'Please select a leave type' }]}
          >
            <Select
              placeholder="Select Leave Type"
              showSearch
              optionFilterProp="children"
              onChange={handleLeaveTypeChange}
            >
              {leaveTypes.map((lt) => (
                <Select.Option key={lt.id} value={lt.leave_type}>
                  {lt.leave_type}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {selectedPaymentStatus && (
            <Form.Item label="Payment Status">
              <Input value={selectedPaymentStatus} readOnly />
            </Form.Item>
          )}

          <Form.Item name="reason" label="Reason (Optional)">
            <TextArea placeholder="Enter reason (optional)" rows={3} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default LeaveRequest;
